import { useCallback, useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage, fmtDate, titleCase } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, Input, Select, Textarea } from "@/components/common/Field";
import { FormDialog } from "@/components/vehicles/VehicleFormDialog";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState, Spinner } from "@/components/common/Primitives";

function useOptions(field) {
  const [opts, setOpts] = useState([]);
  useEffect(() => {
    if (field.type === "vehicle") api.get("/vehicles").then((r) => setOpts(r.data.map((v) => ({ value: v.id, label: `${v.make} ${v.model} · ${v.registration_no}` }))));
    else if (field.type === "ref") api.get(field.endpoint).then((r) => setOpts(r.data.map((x) => ({ value: x.id, label: field.labelOf(x) }))));
  }, [field]);
  return opts;
}

function DynamicField({ field, value, onChange, id }) {
  const opts = useOptions(field);
  const tid = `${id}-${field.key}-input`;
  const common = { "data-testid": tid, value: value ?? "", onChange: (e) => onChange(e.target.value), required: field.required };
  let control;
  if (field.type === "enum") control = <Select {...common}>{!field.required && <option value="">—</option>}{field.options.map((o) => <option key={o} value={o}>{titleCase(o)}</option>)}</Select>;
  else if (field.type === "vehicle" || field.type === "ref") control = <Select {...common}><option value="">{field.required ? "Select…" : "—"}</option>{opts.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</Select>;
  else if (field.type === "text") control = <Textarea {...common} />;
  else if (field.type === "bool") control = <Select {...common} value={value ? "true" : "false"} onChange={(e) => onChange(e.target.value === "true")}><option value="false">No</option><option value="true">Yes</option></Select>;
  else control = <Input {...common} type={field.type === "num" ? "number" : field.type === "date" ? "date" : "text"} step={field.type === "num" ? "any" : undefined} />;
  return <Field label={field.label} required={field.required} className={field.wide ? "sm:col-span-2" : ""}>{control}</Field>;
}

export function ResourceForm({ cfg, open, onClose, item, onSaved, fixed = {} }) {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF(item ? { ...item } : { ...Object.fromEntries(cfg.fields.filter((x) => x.default !== undefined).map((x) => [x.key, x.default])), ...fixed }); }, [open, item, cfg, fixed]);
  const submit = async () => {
    const body = {};
    cfg.fields.forEach((x) => { if (f[x.key] !== undefined && !(item && x.readonlyOnEdit)) body[x.key] = f[x.key] === "" ? null : f[x.key]; });
    Object.assign(body, fixed);
    item ? await api.patch(`${cfg.endpoint}/${item.id}`, body) : await api.post(cfg.endpoint, body);
    toast.success(item ? `${cfg.singular} updated` : `${cfg.singular} added`);
    onSaved();
  };
  return (
    <FormDialog open={open} onClose={onClose} title={item ? `Edit ${cfg.singular.toLowerCase()}` : `Add ${cfg.singular.toLowerCase()}`} onSubmit={submit} testId={`${cfg.id}-form-dialog`}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {cfg.fields.filter((x) => !(fixed[x.key] !== undefined) && !(item && x.hideOnEdit)).map((x) => <DynamicField key={x.key} id={cfg.id} field={x} value={f[x.key]} onChange={(v) => setF({ ...f, [x.key]: v })} />)}
      </div>
    </FormDialog>
  );
}

export function ResourceList({ cfg, rows, reload, onSelect, canEdit, canDelete }) {
  const [edit, setEdit] = useState(null);
  const remove = async (r) => {
    if (!window.confirm(`Delete this ${cfg.singular.toLowerCase()}?`)) return;
    try { await api.delete(`${cfg.endpoint}/${r.id}`); toast.success("Deleted"); reload(); } catch (e) { toast.error(errorMessage(e)); }
  };
  if (!rows) return <Spinner />;
  if (rows.length === 0) return <EmptyState testId={`${cfg.id}-empty`} title={`No ${cfg.title.toLowerCase()} yet`} description={cfg.empty} />;
  return (
    <>
      <div className="fv-card divide-y p-1" data-testid={`${cfg.id}-list`}>
        {rows.map((r) => (
          <div key={r.id} className={`fv-dense-row ${onSelect ? "" : "cursor-default"}`} data-testid={`${cfg.id}-row-${r.id}`} onClick={() => onSelect?.(r)}>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{cfg.primary(r)}</p>
              <p className="truncate text-[11px] text-muted-foreground">{cfg.secondary(r)}</p>
            </div>
            {cfg.badge && r[cfg.badge] && <StatusBadge status={r[cfg.badge]} testId={`${cfg.id}-status-${r.id}`} />}
            {cfg.extraBadge && cfg.extraBadge(r)}
            {canEdit && <button onClick={(e) => { e.stopPropagation(); setEdit(r); }} data-testid={`${cfg.id}-edit-${r.id}`} className="rounded-lg p-1.5 text-primary hover:bg-primary/10" aria-label="Edit"><Pencil className="h-4 w-4" /></button>}
            {canDelete && <button onClick={(e) => { e.stopPropagation(); remove(r); }} data-testid={`${cfg.id}-delete-${r.id}`} className="rounded-lg p-1.5 text-destructive hover:bg-destructive/10" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>}
          </div>
        ))}
      </div>
      <ResourceForm cfg={cfg} open={!!edit} onClose={() => setEdit(null)} item={edit} onSaved={() => { setEdit(null); reload(); }} />
    </>
  );
}

export function ResourceSection({ cfg, params = {}, fixed = {}, onSelect, header = true }) {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => api.get(cfg.endpoint, { params: { ...params, ...(filter ? { [cfg.badge]: filter } : {}) } }).then((r) => setRows(r.data)), [cfg, params, filter]);
  useEffect(() => { load(); }, [load]);
  const canEdit = can(...cfg.editRoles);
  return (
    <div className="space-y-3">
      {header && (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {cfg.statusOptions && <Select data-testid={`${cfg.id}-status-filter`} className="h-9 w-auto" value={filter} onChange={(e) => setFilter(e.target.value)}><option value="">All statuses</option>{cfg.statusOptions.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}</Select>}
            {rows && <span className="text-sm text-muted-foreground">{rows.length} {rows.length === 1 ? cfg.singular.toLowerCase() : cfg.title.toLowerCase()}</span>}
          </div>
          {canEdit && <button onClick={() => setOpen(true)} data-testid={`add-${cfg.id}-btn`} className="flex h-9 items-center gap-1 rounded-full bg-primary px-3 text-sm font-semibold text-primary-foreground hover:bg-[#0071e3]"><Plus className="h-4 w-4" />Add {cfg.singular.toLowerCase()}</button>}
        </div>
      )}
      <ResourceList cfg={cfg} rows={rows} reload={load} onSelect={onSelect} canEdit={canEdit} canDelete={can(...(cfg.deleteRoles || ["admin", "fleet_manager"]))} />
      <ResourceForm cfg={cfg} open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} fixed={fixed} />
    </div>
  );
}

export const money = (n) => (n == null ? "—" : `₹${Number(n).toLocaleString("en-IN")}`);
export { fmtDate };
